// src/components/JourneyCompositionMatrix.jsx — CHANTIER OTA Lot O2
//
// UI de la matrice de composition de l'espace voyageur (blocs formulaire /
// caution / livret / reçu / état des lieux × plateformes booking / airbnb /
// direct), en deux
// consommateurs :
//   - JourneyCompositionGlobalCard    : Paramètres → carte « Espace voyageur »
//     (matrice GLOBALE par plateforme + liste des exceptions actives avec liens)
//   - JourneyCompositionLogementSection : fiche logement → Info FBZ → carte
//     Administration (« Suivre le réglage global » OU « Personnaliser » la même
//     matrice pour CE logement, badge si personnalisé, « Revenir au global »)
//
// Contrat d'API (api/beds24-mapping.js, requireAdmin) :
//   { action: 'journey_composition_get', logement_id? }
//     → { defaults, global, effective_global, overrides, logement? }
//   { action: 'journey_composition_set', scope, plateforme, blocs }
//     → { ok: true, row }
//
// Cascade (résolue serveur par lib/journey-composition-resolver.js) :
//   override LOGEMENT > matrice GLOBALE > défauts codés ; 'auto' = déléguer.
// « Revenir au global » = enregistrer TOUT 'auto' (jamais de suppression).
//
// Sécurité UI : pas d'auth client (JWT admin transmis, requireAdmin serveur) ;
// toast d'échec OBLIGATOIRE sur erreur API (anti fire-and-forget silencieux).

import { useState, useEffect, useCallback } from "react";
import { DS } from "../lib/ui";
import { supabase } from "../supabase";

export const JC_PLATEFORMES = ["booking", "airbnb", "direct"];
// etat_lieux = 5e bloc (décision Dimitri 2026-07-21, cadrage État des lieux) —
// défaut codé 'on' pour les 3 plateformes. Décision produit post-démo : le
// futur module état des lieux voyageur vivra DANS le livret d'accueil (pas
// comme bloc séparé de l'espace voyageur OTA) — la colonne pilote cette
// future section du livret.
// avis = 6e bloc (système d'avis « option a », 2026-07-22) : pilote le prompt
// « comment s'est passé votre séjour ? » en phase départ. DORMANT : défaut codé
// 'off' partout ; même réglé 'on' ici, la feature reste INERTE tant que le flag
// serveur AVIS_GATING_ENABLED n'est pas posé (gate avocat). Le texte d'aide
// ci-dessous dit l'état réel (règle « textes = état actuel »).
export const JC_BLOCS = [
  "formulaire",
  "caution",
  "livret",
  "recu",
  "etat_lieux",
  "avis",
];

export const JC_PLATEFORME_LABELS = {
  booking: "Booking",
  airbnb: "Airbnb",
  direct: "Direct",
};
export const JC_BLOC_LABELS = {
  formulaire: "Formulaire",
  caution: "Caution",
  livret: "Livret",
  recu: "Reçu",
  etat_lieux: "État des lieux",
  avis: "Avis",
};

// Sous-libellés d'en-tête (décision produit post-démo) : le module état des
// lieux voyageur vivra DANS le livret d'accueil, pas comme bloc séparé de
// l'espace voyageur OTA — la colonne pilote la future section du livret.
export const JC_BLOC_SUBLABELS = {
  etat_lieux: "s'affichera dans le livret d'accueil",
  // État réel : le prompt ressenti est INERTE tant que le flag serveur n'est
  // pas posé (gate avocat) — réglable ici, sans effet tant qu'inactif.
  avis: "inactif tant que non activé par l'équipe",
};

// Forme canonique d'une matrice tout-'auto' (état « suit le niveau inférieur »).
export function allAutoRows() {
  const rows = {};
  for (const p of JC_PLATEFORMES) {
    rows[p] = {};
    for (const b of JC_BLOCS) rows[p][b] = "auto";
  }
  return rows;
}

// PUR — une matrice {plateforme:{bloc:mode}} est « personnalisée » si au moins
// une cellule ≠ 'auto' (miroir client de hasActiveOverride serveur).
export function isCustomizedRows(rows) {
  if (!rows || typeof rows !== "object") return false;
  return JC_PLATEFORMES.some((p) =>
    JC_BLOCS.some((b) => rows[p] && rows[p][b] && rows[p][b] !== "auto"),
  );
}

// PUR — état EFFECTIF d'une cellule pour l'AFFICHAGE (badge Activé/Désactivé) :
// mode de la cellule, sinon mode du niveau inférieur (global pour un override
// logement, 'auto' pour la matrice globale), sinon défaut codé.
// ⚠️ MIROIR CLIENT de la cascade de lib/journey-composition-resolver.js
// (CRA/webpack interdit d'importer hors src/ — ModuleScopePlugin) : la parité
// exacte est VERROUILLÉE par un test qui compare cette fonction à
// resolveJourneyComposition sur toutes les combinaisons
// (JourneyCompositionMatrix.test.jsx). Les défauts par plateforme ne sont
// JAMAIS dupliqués ici : ils viennent du GET (CODED_DEFAULTS serveur).
export function effectiveCell(mode, lowerMode, defaultOn) {
  if (mode === "on") return true;
  if (mode === "off") return false;
  if (lowerMode === "on") return true;
  if (lowerMode === "off") return false;
  return !!defaultOn;
}

async function callBeds24Mapping(body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Pas de session admin active. Reconnecte-toi.");
  const r = await fetch("/api/beds24-mapping", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
}

const cellSelectStyle = {
  border: "1px solid #dde3ec",
  borderRadius: 7,
  padding: "5px 6px",
  fontSize: 12,
  outline: "none",
  background: "#f8fafd",
  fontFamily: DS.fontBody,
  width: "100%",
  minWidth: 86,
};

// ── Matrice 3 plateformes × 5 blocs ──────────────────────────────────────────
// rows = {plateforme: {bloc: 'auto'|'on'|'off'}} (draft contrôlé par le parent)
// lowerRows = niveau inférieur pour le hint Auto (matrice globale pour un
//   override logement ; null pour la matrice globale elle-même)
// defaults = défauts codés {plateforme: {bloc: bool}} (fournis par le GET)
export function JourneyCompositionMatrixGrid({
  rows,
  lowerRows,
  defaults,
  onCellChange,
  disabled,
  testIdPrefix = "jc",
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        data-testid={`${testIdPrefix}-matrix`}
        style={{ borderCollapse: "collapse", width: "100%", minWidth: 580 }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: "left",
                fontSize: 11,
                color: "#666",
                padding: "6px 8px",
                fontWeight: 600,
              }}
            >
              Plateforme
            </th>
            {JC_BLOCS.map((b) => (
              <th
                key={b}
                title={
                  b === "etat_lieux"
                    ? "S'affichera dans le livret d'accueil — module à venir, réglage pris en compte à son activation."
                    : undefined
                }
                style={{
                  textAlign: "left",
                  fontSize: 11,
                  color: "#666",
                  padding: "6px 8px",
                  fontWeight: 600,
                }}
              >
                {JC_BLOC_LABELS[b]}
                {JC_BLOC_SUBLABELS[b] && (
                  <div
                    data-testid={`${testIdPrefix}-sublabel-${b}`}
                    style={{
                      fontWeight: 400,
                      fontSize: 9.5,
                      color: "#8a93a5",
                      marginTop: 1,
                    }}
                  >
                    {JC_BLOC_SUBLABELS[b]}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {JC_PLATEFORMES.map((p) => (
            <tr key={p} style={{ borderTop: "1px solid #eef1f6" }}>
              <td
                style={{
                  padding: "8px 8px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: DS.navy,
                  whiteSpace: "nowrap",
                }}
              >
                {JC_PLATEFORME_LABELS[p]}
              </td>
              {JC_BLOCS.map((b) => {
                const mode = (rows[p] && rows[p][b]) || "auto";
                const lowerMode =
                  (lowerRows && lowerRows[p] && lowerRows[p][b]) || "auto";
                const defaultOn = !!(defaults && defaults[p] && defaults[p][b]);
                const eff = effectiveCell(mode, lowerMode, defaultOn);
                return (
                  <td
                    key={b}
                    style={{ padding: "6px 8px", verticalAlign: "top" }}
                  >
                    {/* LISIBILITÉ (retour démo Dimitri) : ce qu'on LIT en
                        balayant la matrice = l'état EFFECTIF, gros et en
                        couleur (badge sauge/gris du DS admin) ; le mode en
                        petit à côté ((auto)/(forcé)). Le <select> dessous
                        reste le contrôle auto/on/off. */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 5,
                        marginBottom: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        data-testid={`${testIdPrefix}-eff-${p}-${b}`}
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          background: eff ? DS.successBg : "#eef1f4",
                          color: eff ? DS.successText : DS.textSecondary,
                        }}
                      >
                        {eff ? "Activé" : "Désactivé"}
                      </span>
                      <span
                        data-testid={`${testIdPrefix}-mode-${p}-${b}`}
                        style={{ fontSize: 10, color: "#8a93a5" }}
                      >
                        {mode === "auto" ? "(auto)" : "(forcé)"}
                      </span>
                    </div>
                    <select
                      value={mode}
                      disabled={disabled}
                      data-testid={`${testIdPrefix}-cell-${p}-${b}`}
                      onChange={(e) => onCellChange(p, b, e.target.value)}
                      style={cellSelectStyle}
                    >
                      <option value="auto">Auto</option>
                      <option value="on">Forcer : activé</option>
                      <option value="off">Forcer : désactivé</option>
                    </select>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Toast({ toast, testIdPrefix }) {
  if (!toast) return null;
  return (
    <div
      role="status"
      data-testid={`${testIdPrefix}-toast-${toast.type}`}
      style={{
        marginTop: 10,
        padding: "8px 12px",
        borderRadius: 7,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        background: toast.type === "error" ? "#fce8e8" : "#e8f5e9",
        color: toast.type === "error" ? DS.red : DS.green,
      }}
    >
      {toast.message}
    </div>
  );
}

function SaveButton({
  saving,
  disabled,
  onClick,
  testId,
  label = "💾 Enregistrer",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving || disabled}
      data-testid={testId}
      style={{
        background: saving || disabled ? "#cbd5e1" : DS.navy,
        color: "#fff",
        border: "none",
        borderRadius: 7,
        padding: "8px 16px",
        fontSize: 12,
        fontWeight: 700,
        cursor: saving || disabled ? "not-allowed" : "pointer",
        fontFamily: DS.fontBody,
      }}
    >
      {saving ? "⏳ Enregistrement…" : label}
    </button>
  );
}

// Enregistre séquentiellement les plateformes dont le draft diffère du saved.
async function saveChangedRows(scope, draft, saved) {
  for (const p of JC_PLATEFORMES) {
    const changed = JC_BLOCS.some(
      (b) =>
        ((draft[p] || {})[b] || "auto") !== ((saved[p] || {})[b] || "auto"),
    );
    if (!changed) continue;
    await callBeds24Mapping({
      action: "journey_composition_set",
      scope,
      plateforme: p,
      blocs: draft[p],
    });
  }
}

// ── Paramètres → carte « Espace voyageur » (matrice GLOBALE) ─────────────────
// resolveLogName : (logement_id) → nom affichable (fallback id).
// onOpenLogement : callback lien exception → fiche logement (optionnel).
export function JourneyCompositionGlobalCard({
  resolveLogName,
  onOpenLogement,
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(allAutoRows());
  const [saved, setSaved] = useState(allAutoRows());
  const [defaults, setDefaults] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const json = await callBeds24Mapping({
        action: "journey_composition_get",
      });
      // Garde : sans les défauts serveur, les badges « effectif » mentiraient
      // (tout auto affiché Désactivé) → on refuse d'afficher la matrice.
      if (!json.defaults) {
        throw new Error("Réponse serveur incomplète (défauts absents)");
      }
      setDraft(json.global || allAutoRows());
      setSaved(json.global || allAutoRows());
      setDefaults(json.defaults);
      setOverrides((json.overrides || []).filter((o) => o.active));
    } catch (e) {
      setLoadError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = JC_PLATEFORMES.some((p) =>
    JC_BLOCS.some(
      (b) =>
        ((draft[p] || {})[b] || "auto") !== ((saved[p] || {})[b] || "auto"),
    ),
  );

  async function handleSave() {
    setSaving(true);
    setToast(null);
    try {
      await saveChangedRows("global", draft, saved);
      setSaved(draft);
      setToast({ type: "success", message: "✅ Matrice globale enregistrée." });
    } catch (e) {
      setToast({
        type: "error",
        message: `❌ Échec enregistrement : ${e.message || String(e)}`,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="jc-global-card">
      <div
        style={{
          fontSize: 12,
          color: "#666",
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Compose l'espace voyageur (formulaire d'arrivée, caution, livret, reçu,
        état des lieux, avis) selon la plateforme de réservation. Chaque cellule
        affiche l'état <strong>effectif</strong> (Activé/Désactivé) :{" "}
        <strong>(auto)</strong> = défaut FBZ (Booking : tout sauf Avis · Airbnb :
        tout sauf Caution et Avis · Direct : tout sauf Avis),{" "}
        <strong>(forcé)</strong> = réglé ici.{" "}
        <strong>
          ⚠️ Ces réglages sont appliqués immédiatement, en production, y compris
          sur la ligne Direct.
        </strong>{" "}
        Ce que chaque cellule change aujourd'hui :{" "}
        <strong>Caution</strong> sur « Désactivé » coupe la caution du parcours —
        le paiement de caution est refusé, aucune pré-autorisation Stripe n'est
        créée, la caution disparaît de l'espace voyageur, le contrat bascule sur
        la variante « aucun dépôt demandé » et la caution cesse de conditionner
        l'accès au livret. <strong>Reçu</strong> pilote le bloc reçu de l'espace
        voyageur en phase départ. <strong>État des lieux</strong> pilote l'état
        des lieux voyageur (accès et affichage dans le livret).{" "}
        <strong>Avis</strong> pilote la demande d'avis, qui reste en outre
        soumise à l'interrupteur serveur AVIS_GATING_ENABLED.{" "}
        <strong>Formulaire</strong> et <strong>Livret</strong> sont enregistrés
        mais aucun code ne les lit à ce jour : les régler ne change rien.{" "}
        <strong>
          Les rappels de caution ne consultent pas cette matrice : ils partent
          même si la cellule Caution est sur « Désactivé ».
        </strong>
      </div>

      {loading && (
        <div style={{ fontSize: 12, color: "#888" }}>⏳ Chargement…</div>
      )}

      {!loading && loadError && (
        <div
          style={{
            padding: "8px 10px",
            background: "#fce8e8",
            color: DS.red,
            borderRadius: 7,
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span>⚠️ Lecture impossible : {loadError}</span>
          <button
            type="button"
            onClick={load}
            style={{
              background: "#fff",
              border: `1px solid ${DS.red}55`,
              color: DS.red,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <JourneyCompositionMatrixGrid
            rows={draft}
            lowerRows={null}
            defaults={defaults}
            disabled={saving}
            testIdPrefix="jc-global"
            onCellChange={(p, b, v) =>
              setDraft((prev) => ({ ...prev, [p]: { ...prev[p], [b]: v } }))
            }
          />
          <div style={{ marginTop: 10 }}>
            <SaveButton
              saving={saving}
              disabled={!dirty}
              onClick={handleSave}
              testId="jc-global-save"
            />
          </div>

          {/* Exceptions actives (overrides par logement, au moins 1 cellule ≠ auto) */}
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: DS.navy,
                marginBottom: 6,
              }}
            >
              Exceptions par logement
            </div>
            {overrides.length === 0 && (
              <div
                data-testid="jc-no-overrides"
                style={{ fontSize: 12, color: "#888" }}
              >
                Aucune exception : tous les logements suivent le réglage global.
              </div>
            )}
            {overrides.length > 0 && (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {overrides.map((o) => (
                  <li
                    key={o.logement_id}
                    data-testid={`jc-override-${o.logement_id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 0",
                      fontSize: 12,
                      color: "#444",
                    }}
                  >
                    <span
                      style={{
                        background: "#fdf1ec",
                        color: "#b0562f",
                        borderRadius: 6,
                        padding: "2px 8px",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Personnalisé
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onOpenLogement && onOpenLogement(o.logement_id)
                      }
                      data-testid={`jc-override-link-${o.logement_id}`}
                      title="Ouvrir la fiche logement (Info FBZ → Administration)"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        color: DS.navy,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: onOpenLogement ? "pointer" : "default",
                        textDecoration: onOpenLogement ? "underline" : "none",
                        fontFamily: DS.fontBody,
                      }}
                    >
                      {(resolveLogName && resolveLogName(o.logement_id)) ||
                        o.logement_id}
                    </button>
                    {o.updated_by && (
                      <span style={{ color: "#999", fontSize: 11 }}>
                        — modifié par {o.updated_by}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <Toast toast={toast} testIdPrefix="jc-global" />
    </div>
  );
}

// ── Fiche logement → Info FBZ → Administration → « Espace voyageur » ─────────
export function JourneyCompositionLogementSection({ logementId }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // mode : 'global' (suivre le réglage global, défaut) | 'custom' (personnaliser)
  const [mode, setMode] = useState("global");
  const [customized, setCustomized] = useState(false); // état ENREGISTRÉ (badge)
  const [draft, setDraft] = useState(allAutoRows());
  const [saved, setSaved] = useState(allAutoRows());
  const [globalRows, setGlobalRows] = useState(allAutoRows());
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!logementId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const json = await callBeds24Mapping({
        action: "journey_composition_get",
        logement_id: logementId,
      });
      // Même garde que la vue globale : défauts absents → matrice non affichée.
      if (!json.defaults) {
        throw new Error("Réponse serveur incomplète (défauts absents)");
      }
      const rows = (json.logement && json.logement.rows) || allAutoRows();
      const isCustom = !!(json.logement && json.logement.customized);
      setDraft(rows);
      setSaved(rows);
      setGlobalRows(json.global || allAutoRows());
      setDefaults(json.defaults);
      setCustomized(isCustom);
      setMode(isCustom ? "custom" : "global");
    } catch (e) {
      setLoadError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [logementId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = JC_PLATEFORMES.some((p) =>
    JC_BLOCS.some(
      (b) =>
        ((draft[p] || {})[b] || "auto") !== ((saved[p] || {})[b] || "auto"),
    ),
  );

  async function handleSave() {
    setSaving(true);
    setToast(null);
    try {
      await saveChangedRows(logementId, draft, saved);
      setSaved(draft);
      setCustomized(isCustomizedRows(draft));
      setToast({
        type: "success",
        message: "✅ Réglage espace voyageur enregistré pour ce logement.",
      });
    } catch (e) {
      setToast({
        type: "error",
        message: `❌ Échec enregistrement : ${e.message || String(e)}`,
      });
    } finally {
      setSaving(false);
    }
  }

  // « Revenir au global » = enregistrer TOUT 'auto' (équivalent « suivre le
  // global » — aucune suppression de ligne, décision Lot O2).
  async function handleRevert() {
    setSaving(true);
    setToast(null);
    try {
      const auto = allAutoRows();
      await saveChangedRows(logementId, auto, saved);
      setDraft(auto);
      setSaved(auto);
      setCustomized(false);
      setMode("global");
      setToast({
        type: "success",
        message: "✅ Ce logement suit à nouveau le réglage global.",
      });
    } catch (e) {
      setToast({
        type: "error",
        message: `❌ Échec du retour au global : ${e.message || String(e)}`,
      });
    } finally {
      setSaving(false);
    }
  }

  if (!logementId) return null;

  return (
    <div data-testid="jc-logement-section">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: DS.navy }}>
          Composition de l'espace voyageur
        </span>
        {customized && (
          <span
            data-testid="jc-logement-badge"
            style={{
              background: "#fdf1ec",
              color: "#b0562f",
              borderRadius: 6,
              padding: "2px 8px",
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            Personnalisé
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#666",
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        Blocs de l'espace voyageur (formulaire, caution, livret, reçu, état des
        lieux, avis) pour CE logement. Chaque cellule affiche l'état{" "}
        <strong>effectif</strong> (Activé/Désactivé) ; <strong>(auto)</strong> =
        suit le global puis le défaut FBZ, <strong>(forcé)</strong> = réglé ici.
        Par défaut, ce logement suit le réglage global (Paramètres → Espace
        voyageur). Personnaliser = exception rare, à réserver aux cas justifiés.{" "}
        <strong>
          ⚠️ Ces réglages sont appliqués immédiatement, en production, y compris
          sur la ligne Direct.
        </strong>{" "}
        Ce que chaque cellule change aujourd'hui, pour ce logement :{" "}
        <strong>Caution</strong> sur « Désactivé » coupe la caution du parcours —
        le paiement de caution est refusé, aucune pré-autorisation Stripe n'est
        créée, la caution disparaît de l'espace voyageur, le contrat bascule sur
        la variante « aucun dépôt demandé » et la caution cesse de conditionner
        l'accès au livret. <strong>Reçu</strong> pilote le bloc reçu de l'espace
        voyageur en phase départ. <strong>État des lieux</strong> pilote l'état
        des lieux voyageur (accès et affichage dans le livret).{" "}
        <strong>Avis</strong> pilote la demande d'avis, qui reste en outre
        soumise à l'interrupteur serveur AVIS_GATING_ENABLED.{" "}
        <strong>Formulaire</strong> et <strong>Livret</strong> sont enregistrés
        mais aucun code ne les lit à ce jour : les régler ne change rien.{" "}
        <strong>
          Les rappels de caution ne consultent pas cette matrice : ils partent
          même si la cellule Caution est sur « Désactivé ».
        </strong>
      </div>

      {loading && (
        <div style={{ fontSize: 12, color: "#888" }}>⏳ Chargement…</div>
      )}

      {!loading && loadError && (
        <div
          style={{
            padding: "8px 10px",
            background: "#fce8e8",
            color: DS.red,
            borderRadius: 7,
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span>⚠️ Lecture impossible : {loadError}</span>
          <button
            type="button"
            onClick={load}
            style={{
              background: "#fff",
              border: `1px solid ${DS.red}55`,
              color: DS.red,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginBottom: 10,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "#333",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name={`jc-mode-${logementId}`}
                checked={mode === "global"}
                data-testid="jc-mode-global"
                onChange={() => setMode("global")}
                disabled={saving}
              />
              Suivre le réglage global (défaut)
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "#333",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name={`jc-mode-${logementId}`}
                checked={mode === "custom"}
                data-testid="jc-mode-custom"
                onChange={() => setMode("custom")}
                disabled={saving}
              />
              Personnaliser pour ce logement (exception rare)
            </label>
          </div>

          {mode === "global" && customized && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                background: "#fff8f3",
                border: "1px solid #f3ddd0",
                borderRadius: 7,
                fontSize: 12,
                color: "#8a5636",
                marginBottom: 6,
              }}
            >
              <span>
                Une personnalisation est encore enregistrée pour ce logement.
              </span>
              <button
                type="button"
                onClick={handleRevert}
                disabled={saving}
                data-testid="jc-revert"
                style={{
                  background: "#fff",
                  border: "1px solid #d9a583",
                  color: "#b0562f",
                  borderRadius: 6,
                  padding: "5px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                {saving ? "⏳…" : "Revenir au global"}
              </button>
            </div>
          )}

          {mode === "custom" && (
            <>
              <JourneyCompositionMatrixGrid
                rows={draft}
                lowerRows={globalRows}
                defaults={defaults}
                disabled={saving}
                testIdPrefix="jc-logement"
                onCellChange={(p, b, v) =>
                  setDraft((prev) => ({ ...prev, [p]: { ...prev[p], [b]: v } }))
                }
              />
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 10,
                  flexWrap: "wrap",
                }}
              >
                <SaveButton
                  saving={saving}
                  disabled={!dirty}
                  onClick={handleSave}
                  testId="jc-logement-save"
                />
                {customized && (
                  <button
                    type="button"
                    onClick={handleRevert}
                    disabled={saving}
                    data-testid="jc-revert-custom"
                    style={{
                      background: "#fff",
                      border: "1px solid #d9a583",
                      color: "#b0562f",
                      borderRadius: 7,
                      padding: "8px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: saving ? "not-allowed" : "pointer",
                      fontFamily: DS.fontBody,
                    }}
                  >
                    Revenir au global
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}

      <Toast toast={toast} testIdPrefix="jc-logement" />
    </div>
  );
}
